/**
 * The mock payment driver's own page. Handover §9.4 steps 2 to 4.
 *
 * It is not a payment page and nothing on it may be mistaken for one: the bar
 * at the top says so in both languages, no amount is taken, no card field
 * exists and nothing here writes to the order.
 *
 * What it does is exactly what SimplePay does. Each button drives one outcome
 * through `payment_mock`, the mock driver's test API, which **posts a signed
 * IPN to the real IPN endpoint** — the same endpoint, the same signature
 * scheme and the same refusals a card payment will meet. Then it sends the
 * customer to the back URL, `catering.planteen.hu/fizetes/{token}`, which
 * §9.4 says is display only and never changes the order's status. The status
 * changes on the IPN and on nothing else (§9.4 step 4).
 *
 * The IPN can be slower than the redirect, which is §9.10 no. 3: the return
 * page polls and says *Fizetés feldolgozás alatt* until it arrives. So the
 * button does not wait for the order to move — only for the test API to have
 * accepted the message.
 *
 * It is reached only while `PAYMENT_DRIVER=mock`: with the real driver,
 * `payment_start` answers with SimplePay's own `paymentUrl` and the customer
 * never comes here. `payment_mock` refuses outright in that case.
 */

import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { readPublicConfig } from '../lib/env.ts'
import { driveMockPayment } from '../lib/payment.ts'

import { formatter } from '../page/format.ts'
import { langFromSearch, useDocumentLang } from '../page/documentLang.ts'
import { translator } from '../page/lang.ts'

export type MockResult = 'success' | 'fail' | 'cancel'

export function MockPaymentPage(): JSX.Element {
  const { token = '' } = useParams()
  const [search] = useSearchParams()
  const navigate = useNavigate()
  const lang = langFromSearch(search)
  const t = translator(lang)
  useDocumentLang(lang)
  const f = formatter(lang)
  const amount = Number(search.get('amount') ?? 0)
  /* §9.4: `order_ref-v{version}-a{attempt}`. `payment_start` put it on this
     page's URL, because it is the provider's handle on the transaction and
     the IPN is addressed by it. */
  const providerRef = search.get('ref') ?? ''
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  const go = (result: MockResult): void => {
    if (busy) return
    setBusy(true)
    setFailed(false)
    const back = (): void => {
      navigate(`/fizetes/${encodeURIComponent(token)}?result=${result}&lang=${lang}`)
    }
    if (providerRef === '') {
      // Nothing to address an IPN to. The back URL is still display only, so
      // going there is honest: the order simply has not moved.
      back()
      return
    }
    void driveMockPayment(readPublicConfig(), providerRef, result, amount)
      .then((answer) => {
        if (!answer.ok) {
          setBusy(false)
          setFailed(true)
          return
        }
        back()
      })
      .catch(() => {
        setBusy(false)
        setFailed(true)
      })
  }

  return (
    <>
      <div className="mockbar">
        {t(
          'TESZT fizetési oldal — nem a SimplePay. Itt nem történik fizetés, és pénz nem mozdul.',
          'TEST payment page — not SimplePay. No payment happens here and no money moves.',
        )}
      </div>
      <main className="payshell" data-testid="mock-payment">
        <h1>{t('Fizetés (teszt)', 'Payment (test)')}</h1>
        <p className="sub">
          {t(
            'Ezt az oldalt a fizetési szolgáltató helyett mutatjuk, amíg a valódi összeköttetés el nem készül. Válassza ki, mit szeretne kipróbálni.',
            'This page stands in for the payment provider until the real connection is built. Choose what you would like to try.',
          )}
        </p>
        <div className="card">
          <div className="sumrow big">
            <span>{t('Fizetendő', 'Payable')}</span>
            <span className="num">{amount > 0 ? f.money(amount) : '—'}</span>
          </div>
          <p className="sub" style={{ marginBottom: 0 }}>
            {/* Not *Rendelésazonosító*, which is what the PLT-… reference is
                called on the confirmation and on the return page. This is the
                payment token, and two things cannot share one name. */}
            {t('Fizetési azonosító', 'Payment token')}: <span className="num">{token}</span>
          </p>
        </div>
        <div className="acts">
          <button
            type="button"
            className="cta"
            data-result="success"
            disabled={busy}
            onClick={() => go('success')}
          >
            {t('Sikeres fizetés', 'Successful payment')}
          </button>
          <button
            type="button"
            className="cta stop"
            data-result="fail"
            disabled={busy}
            onClick={() => go('fail')}
          >
            {t('Sikertelen fizetés', 'Failed payment')}
          </button>
          <button
            type="button"
            className="cta alt"
            data-result="cancel"
            disabled={busy}
            onClick={() => go('cancel')}
          >
            {t('Megszakítom', 'Cancel')}
          </button>
        </div>
        {failed ? (
          <p className="sub" data-testid="mock-payment-error">
            {t(
              'A teszt értesítést nem sikerült elküldeni. Nézze meg, fut-e az IPN végpont.',
              'The test notification could not be sent. Check that the IPN endpoint is running.',
            )}
          </p>
        ) : null}
      </main>
    </>
  )
}
